/**
 * useStructuredData - Schema.org JSON-LD that lets search engines read the school,
 * its formations, events and articles (https://developers.google.com/search/docs/appearance/structured-data).
 *
 * The builders are pure; pages pass the data they already fetched.
 */

type JsonLd = Record<string, unknown>

const SCHEMA_CONTEXT = 'https://schema.org'
const ORGANIZATION_NAME = 'OXO'
/** Google shows at most this many characters of an article headline. */
const HEADLINE_MAX_LENGTH = 110

export interface SchoolInfo {
    slogan?: string
    street?: string
    postalCode?: string
    city?: string
    phone?: string
    email?: string
    logo?: string
    socials?: { url?: string }[]
}

const organizationId = (siteUrl: string) => `${siteUrl}/#organization`

/** Minimal reference to the school, embedded as provider, organizer, author or publisher. */
const organizationRef = (siteUrl: string): JsonLd => ({
    '@type': 'EducationalOrganization',
    '@id': organizationId(siteUrl),
    name: ORGANIZATION_NAME,
    url: `${siteUrl}/`
})

/** Date part of an ISO date-time; the API stores event and article days at midnight UTC. */
const toIsoDate = (iso?: string | null) => iso ? iso.slice(0, 10) : undefined

export const buildOrganizationSchema = (siteUrl: string, school: SchoolInfo): JsonLd => ({
    '@context': SCHEMA_CONTEXT,
    ...organizationRef(siteUrl),
    alternateName: school.slogan || undefined,
    logo: school.logo || undefined,
    email: school.email || undefined,
    telephone: school.phone || undefined,
    address: school.street
        ? {
            '@type': 'PostalAddress',
            streetAddress: school.street,
            postalCode: school.postalCode,
            addressLocality: school.city,
            addressCountry: 'FR'
        }
        : undefined,
    sameAs: (school.socials || []).map(s => s.url).filter(Boolean)
})

export const buildCourseSchema = (siteUrl: string, formation: { title: string, description?: string, url: string }): JsonLd => ({
    '@context': SCHEMA_CONTEXT,
    '@type': 'Course',
    name: formation.title,
    description: formation.description || undefined,
    url: formation.url,
    provider: organizationRef(siteUrl)
})

export const buildEventSchema = (siteUrl: string, event: {
    title: string
    description?: string
    dateRaw: string
    dateEndRaw?: string | null
    location?: string
    image?: string
    url: string
}): JsonLd => ({
    '@context': SCHEMA_CONTEXT,
    '@type': 'Event',
    name: event.title,
    description: event.description || undefined,
    startDate: toIsoDate(event.dateRaw),
    endDate: toIsoDate(event.dateEndRaw || event.dateRaw),
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: event.location
        ? { '@type': 'Place', name: event.location, address: event.location }
        : undefined,
    image: event.image ? [event.image] : undefined,
    url: event.url,
    organizer: organizationRef(siteUrl)
})

export const buildArticleSchema = (siteUrl: string, article: {
    title: string
    description?: string
    dateRaw?: string
    image?: string
    url: string
}, logo?: string): JsonLd => ({
    '@context': SCHEMA_CONTEXT,
    '@type': 'NewsArticle',
    headline: article.title.length > HEADLINE_MAX_LENGTH
        ? `${article.title.slice(0, HEADLINE_MAX_LENGTH - 1)}…`
        : article.title,
    description: article.description || undefined,
    image: article.image ? [article.image] : undefined,
    datePublished: article.dateRaw || undefined,
    mainEntityOfPage: article.url,
    author: organizationRef(siteUrl),
    publisher: {
        ...organizationRef(siteUrl),
        logo: logo ? { '@type': 'ImageObject', url: logo } : undefined
    }
})

/**
 * JSON for an inline script: `<` is escaped so that a text containing `</script>`
 * cannot close the tag. Undefined fields are dropped by JSON.stringify.
 */
export const serializeJsonLd = (schema: JsonLd): string =>
    JSON.stringify(schema).replace(/</g, '\\u003c')

export const useStructuredData = () => {
    const config = useRuntimeConfig()
    const nuxtApp = useNuxtApp()
    const siteUrl = config.public.siteUrl.replace(/\/+$/, '')

    const setStructuredData = (schema: JsonLd) => {
        // Pages call this after awaiting their data, outside the synchronous setup context
        nuxtApp.runWithContext(() => {
            useHead({
                script: [{ type: 'application/ld+json', innerHTML: serializeJsonLd(schema) }]
            })
        })
    }

    /** School info as served by the public API, used for the organization and as article publisher. */
    const fetchSchoolInfo = async (): Promise<SchoolInfo> => {
        try {
            return await $fetch<SchoolInfo>(`${config.public.apiBase}/oxoweb/school/info`)
        } catch (error) {
            console.error('Failed to fetch school info for structured data:', error)
            return {}
        }
    }

    return { siteUrl, setStructuredData, fetchSchoolInfo }
}
